import React from 'react';
import { Sun, Moon, User, Trophy, ArrowLeft } from 'lucide-react';
import { CachedImage } from './CachedImage';

interface AppHeaderProps {
  organizationName: string;
  organizationLogoUrl?: string;
  subtitle?: string;
  user: any;
  isDark: boolean;
  isAdmin: boolean;
  roleLoading?: boolean;
  displayName: string;
  showBackButton?: boolean;
  showBetaBadge?: boolean;
  onToggleTheme: () => void;
  onSignIn: () => void;
  onNavigateHome: () => void;
  onNavigateAdmin?: () => void;
  onOpenProfile?: () => void;
}

export function AppHeader({
  organizationName,
  organizationLogoUrl,
  subtitle,
  user,
  isDark,
  isAdmin,
  roleLoading = false,
  displayName,
  showBackButton = false,
  showBetaBadge = false,
  onToggleTheme,
  onSignIn,
  onNavigateHome,
  onNavigateAdmin,
  onOpenProfile
}: AppHeaderProps) {
  const defaultLogoUrl = `${import.meta.env.VITE_SUPABASE_URL || 'https://ibztzxgdbluqzisqkiry.supabase.co'}/storage/v1/object/public/Organization%20Logos/full-text-primary.png`;

  return (
    <div className="pt-safe px-safe">
        <div className="relative px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
              <CachedImage
                src={organizationLogoUrl || defaultLogoUrl}
                alt={`${organizationName} logo`}
                size="small"
                className="h-12 w-auto sm:h-14 lg:h-16 drop-shadow-sm flex-shrink-0 max-w-[200px]"
                loading="eager"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white drop-shadow-sm truncate">
                    {organizationName}
                  </h1>
                  {showBetaBadge && (
                    <span className="flex-shrink-0 px-2 py-0.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wider bg-white/20 backdrop-blur-sm text-white/90 rounded-full whitespace-nowrap">
                      Beta
                    </span>
                  )}
                </div>
                {subtitle && (
                  <p className="text-xs sm:text-sm text-white/80 drop-shadow-sm hidden sm:block">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              {showBackButton && (
                <button
                  onClick={onNavigateHome}
                  className="p-2 sm:p-2.5 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-200 shadow-lg"
                  aria-label="Back to organization"
                  title="Back to Organization"
                >
                  <ArrowLeft className="h-5 w-5 text-white drop-shadow-sm" />
                </button>
              )}

              {user && !roleLoading && isAdmin && onNavigateAdmin && !showBackButton && (
                <button
                  onClick={onNavigateAdmin}
                  className="p-2 sm:p-2.5 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-200 shadow-lg"
                  aria-label="Admin panel"
                  title="Admin Panel"
                >
                  <Trophy className="h-5 w-5 text-white drop-shadow-sm" />
                </button>
              )}

              <button
                onClick={onToggleTheme}
                className="p-2 sm:p-2.5 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-200 shadow-lg"
                aria-label="Toggle theme"
              >
                {isDark ? (
                  <Sun className="h-5 w-5 text-white drop-shadow-sm" />
                ) : (
                  <Moon className="h-5 w-5 text-white drop-shadow-sm" />
                )}
              </button>

              {user ? (
                <>
                  {/* Mobile: Icon only */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenProfile?.();
                    }}
                    className="sm:hidden p-2 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 active:bg-white/40 transition-all duration-200 shadow-lg touch-manipulation"
                    aria-label="Open profile"
                    title="Profile & Settings"
                  >
                    <User className="h-5 w-5 text-white drop-shadow-sm" />
                  </button>
                  
                  {/* Desktop: Name + Icon */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenProfile?.();
                    }}
                    className="hidden sm:flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-xl px-3 py-2 shadow-lg hover:bg-white/30 active:bg-white/40 transition-all duration-200 touch-manipulation"
                    aria-label="Open profile"
                    title="Profile & Settings"
                  >
                    <div className="bg-white/30 p-1.5 rounded-lg">
                      <User className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-sm font-medium text-white drop-shadow-sm truncate max-w-32 lg:max-w-none">
                      {displayName}
                    </span>
                  </button>
                </>
              ) : (
                <button
                  onClick={onSignIn}
                  className="flex items-center space-x-1.5 sm:space-x-2 px-2.5 sm:px-3 py-2 sm:py-2.5 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-200 shadow-lg text-white font-medium text-sm"
                >
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">Get Started</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
  );
}
